// shadcn
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { cn } from 'src/@/lib/utils';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import NovoContatoForm from 'src/sections/@dashboard/cadastro/NovoContatoForm';
import v2_actionCriarContato from 'src/actions/v2/contatos/v2_actionCriarContato';
import { validarNumeroWhatsapp } from 'src/utils/functions';
import { useSnackbar } from 'notistack';
import { getBuscarContatos } from 'src/redux/slices/contatos';
import { useDispatch } from 'react-redux';
import v2_actionListarListas from 'src/actions/v2/contatos/v2_actionListarListas';
import v2_actionCriarLista from 'src/actions/v2/contatos/v2_actionCriarLista';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Label } from 'src/@/components/ui/label';
import { Input } from 'src/@/components/ui/input';
import { Button } from 'src/@/components/ui/button';

// ----------------------------------------------------------------------

export default function ModalNovoContato({
    user,
    cliente,
    open,
    listaClientesCanais,
    loading,
    callbackAddContact,
    onClose
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [loadingNovoContato, setLoadingNovoContato] = useState(false);
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const [listas, setListas] = useState([]);
    const [selectedListas, setSelectedListas] = useState([]);
    const [showCriarLista, setShowCriarLista] = useState(false);
    const [novaListaNome, setNovaListaNome] = useState('');
    const [loadingCriarLista, setLoadingCriarLista] = useState(false);

    const fetchListas = () => {
        if (cliente?._id) {
            v2_actionListarListas(cliente._id)
                .then(res => setListas(res.data?.data || []))
                .catch(console.log);
        }
    };

    useEffect(() => {
        if (open) {
            fetchListas();
        }
        if (!open) {
            setSelectedListas([]);
            setShowCriarLista(false);
            setNovaListaNome('');
        }
    }, [open, cliente?._id]);

    async function handleCriarLista() {
        if (!novaListaNome.trim()) return;
        try {
            setLoadingCriarLista(true);
            const res = await v2_actionCriarLista({ nome: novaListaNome.trim(), descricao: '', cliente_id: cliente._id });
            if (res.data?.data?._id) {
                setSelectedListas(prev => [...prev, res.data.data._id]);
            }
            enqueueSnackbar(t('chat.newContact.list.createSuccess'), { variant: 'success' });
            setNovaListaNome('');
            setShowCriarLista(false);
            fetchListas();
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('chat.newContact.list.createError'), { variant: 'error' });
        } finally {
            setLoadingCriarLista(false);
        }
    }

    async function handleCriarContato(data) {
        const { contato, endereco, camposPersonalizados } = data;

        setLoadingNovoContato(true);

        try {
            const requestData = {
                scope: {
                    nome: contato.nome,
                    telefone: validarNumeroWhatsapp(contato.telefone),
                    info: {
                        email: contato.email,
                        cpf: contato.cpf,
                        dataNascimento: contato.dataNascimento,
                        endereco,
                    },
                    custom_fields: camposPersonalizados,
                    contact_lists_id: selectedListas,
                },
                cliente_id: cliente._id,
                usuario_id: user.id,
                checkExistContact: true,
            };

            const response = await v2_actionCriarContato(requestData);
            if (response.status === 200) {
                if (response.data.error && response.data.codError === 'CONTACT_EXIST') {
                    enqueueSnackbar(t('chat.newContact.alreadyExists'), { variant: 'warning' });
                    setLoadingNovoContato(false);
                    return;
                }

                setLoadingNovoContato(false);
                onClose();
                enqueueSnackbar(t('chat.newContact.success'), { variant: 'success' });
                dispatch(getBuscarContatos(cliente._id));
                callbackAddContact(response.data.data);
            }
        } catch (error) {
            console.error(error);
            setLoadingNovoContato(false);
            enqueueSnackbar(t('chat.newContact.error'), { variant: 'error' });
        }
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className={cn("w-full sm:max-w-[520px] p-0 overflow-hidden border-border flex flex-col max-h-[85vh]")}>
                <DialogHeader className="px-6 pt-6 pb-4 border-b bg-background shrink-0">
                    <DialogTitle className="text-xl font-semibold flex items-center gap-2 text-foreground">
                        <Iconify icon="mdi:account-plus-outline" className="w-[22px] h-[22px] text-violet-600" />
                        {t('chat.newContact.title')}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-5 p-6 bg-muted/30 overflow-y-auto flex-1">
                    <NovoContatoForm
                        ref={formRef}
                        cliente={cliente}
                        showSaveButton={false}
                        hideSelectEmpresa={true}
                        showSignupButton={false}
                        hideActionButtons={true}
                        loadingNovoContato={loadingNovoContato}
                        callbackAddContact={handleCriarContato}
                    />

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t('chat.newContact.list.linkTo')}
                            </Label>
                            <button
                                type="button"
                                onClick={() => setShowCriarLista(!showCriarLista)}
                                className="flex items-center gap-1 text-xs font-medium text-[#7C3AED] hover:text-[#6D28D9] transition-colors"
                            >
                                <Iconify icon={showCriarLista ? "mdi:close" : "fluent:add-12-filled"} width={14} />
                                {showCriarLista ? t('common.buttons.cancel') : t('chat.newContact.list.newList')}
                            </button>
                        </div>

                        {showCriarLista && (
                            <div className="flex items-center gap-2">
                                <Input
                                    value={novaListaNome}
                                    onChange={(e) => setNovaListaNome(e.target.value)}
                                    placeholder={t('chat.newContact.list.namePlaceholder')}
                                    className="h-8 text-sm border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-lg"
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleCriarLista(); }}
                                />
                                <Button
                                    size="sm"
                                    disabled={loadingCriarLista || !novaListaNome.trim()}
                                    onClick={handleCriarLista}
                                    className="h-8 px-3 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs rounded-lg shrink-0"
                                >
                                    {loadingCriarLista ? <Iconify icon="mdi:loading" className="w-4 h-4 animate-spin" /> : t('common.buttons.create')}
                                </Button>
                            </div>
                        )}

                        {listas.length > 0 ? (
                            <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-gray-200 dark:border-[#383838] p-3 space-y-2 max-h-[140px] overflow-y-auto">
                                {listas.map((lista) => (
                                    <label key={lista._id} className="flex items-center gap-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#383838]/50 rounded-lg px-2 py-1.5 transition-colors">
                                        <Checkbox
                                            checked={selectedListas.includes(lista._id)}
                                            onCheckedChange={(checked) => {
                                                setSelectedListas(prev =>
                                                    checked
                                                        ? [...prev, lista._id]
                                                        : prev.filter(id => id !== lista._id)
                                                );
                                            }}
                                            className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED] h-4 w-4"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-[#D1D2D3]">{lista.nome}</span>
                                    </label>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400 dark:text-[#ABABAD] italic">{t('chat.newContact.list.empty')}</p>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t bg-background shrink-0">
                    <ButtonLoading
                        loading={loadingNovoContato}
                        onClick={() => formRef.current?.submit()}
                        className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white rounded-md flex items-center justify-center gap-2 transition-all font-medium shadow-sm shadow-violet-500/20 hover:shadow-violet-500/40"
                    >
                        <Iconify icon="mdi:account-plus-outline" className="w-[18px] h-[18px]" />
                        {t('chat.newContact.create')}
                    </ButtonLoading>
                </div>
            </DialogContent>
        </Dialog>
    );
}